/**
 * Analytics Service - Centralized GA4 Event Tracking
 *
 * Uses Firebase Analytics (GA4) for event tracking.
 * All events are typed and organized by category for consistency.
 */

import { logEvent, type Analytics } from 'firebase/analytics'
import { analytics } from '@/lib/firebase'

/**
 * Core tracking function - sends events to GA4
 */
function track(eventName: string, params?: Record<string, string | number | boolean>) {
  if (!analytics) {
    console.log('[Analytics] Skipped (not initialized):', eventName, params)
    return
  }

  try {
    logEvent(analytics as Analytics, eventName, params)
    console.log('[Analytics]', eventName, params)
  } catch (error) {
    console.warn('[Analytics] Failed to track event:', eventName, error)
  }
}

// =============================================================================
// FUNNEL EVENTS - Critical conversion tracking
// =============================================================================

export const trackFunnelEvent = {
  /** Survey page mounted */
  surveyStarted: () => track('survey_started'),

  /** User completed all 4 survey steps */
  surveyCompleted: (params: { age: number; skin_type: string; sensitivity: string }) =>
    track('survey_completed', params),

  /** User selected camera or gallery for photo */
  photoSourceSelected: (source: 'camera' | 'gallery') =>
    track('photo_source_selected', { source }),

  /** Photo analysis started */
  photoAnalysisStarted: () => track('photo_analysis_started'),

  /** Photo analysis completed successfully */
  photoAnalysisCompleted: (params: { duration_ms: number; skin_score: number }) =>
    track('photo_analysis_completed', params),

  /** Photo analysis failed */
  photoAnalysisFailed: (error_type: string) =>
    track('photo_analysis_failed', { error_type }),

  /** Teaser page viewed */
  teaserViewed: (has_results: boolean) =>
    track('teaser_viewed', { has_results }),

  /** User clicked CTA on teaser page */
  teaserCtaClicked: () => track('teaser_cta_clicked'),

  /** OAuth login started */
  loginStarted: (provider: 'google' | 'apple') =>
    track('login_started', { provider }),

  /** OAuth login completed */
  loginCompleted: (provider: 'google' | 'apple') =>
    track('login_completed', { provider }),

  /** OAuth login failed */
  loginFailed: (error_code: string) =>
    track('login_failed', { error_code }),

  /** Payment form submitted */
  paymentStarted: (amount: number = 2900) =>
    track('payment_started', { amount }),

  /** Payment succeeded */
  paymentCompleted: (amount: number = 2900) =>
    track('payment_completed', { amount }),

  /** Payment failed */
  paymentFailed: (decline_code: string) =>
    track('payment_failed', { decline_code }),

  /** Full survey completed (post-payment) */
  fullSurveyCompleted: (params: { ethnicity: string; zip_code: string }) =>
    track('full_survey_completed', params),

  /** HD analysis started */
  hdAnalysisStarted: () => track('hd_analysis_started'),

  /** HD analysis completed */
  hdAnalysisCompleted: (duration_ms: number) =>
    track('hd_analysis_completed', { duration_ms }),

  /** Full report page viewed */
  reportViewed: () => track('report_viewed'),
}

// =============================================================================
// ENGAGEMENT EVENTS - User interaction tracking
// =============================================================================

export const trackEngagementEvent = {
  /** User changed a survey field */
  surveyFieldChanged: (field: string, value: string | number) =>
    track('survey_field_changed', { field, value: String(value) }),

  /** User retook photo */
  photoRetake: (from: 'preview' | 'error') =>
    track('photo_retake', { from }),

  /** User expanded a metric card */
  metricExpanded: (metric_name: string) =>
    track('metric_expanded', { metric_name }),

  /** User clicked on a product */
  productClicked: (product_id: string, product_name: string, step: string) =>
    track('product_clicked', { product_id, product_name, step }),

  /** User changed tab on report page */
  tabChanged: (tab: 'analysis' | 'routine' | 'products') =>
    track('tab_changed', { tab }),

  /** User changed price tier */
  priceTierChanged: (tier: 'affordable' | 'mainstream' | 'premium') =>
    track('price_tier_changed', { tier }),

  /** User generated PDF */
  pdfGenerated: () => track('pdf_generated'),

  /** Browser back navigation was prevented */
  backPrevented: (page: string) =>
    track('back_prevented', { page }),
}

// =============================================================================
// ERROR EVENTS - Error tracking for monitoring
// =============================================================================

export const trackError = {
  /** Analysis API error */
  analysis: (error_type: string, message: string) =>
    track('error_analysis', { error_type, message }),

  /** Photo upload error */
  photoUpload: (error_type: string) =>
    track('error_photo_upload', { error_type }),

  /** Auth error */
  auth: (error_code: string) =>
    track('error_auth', { error_code }),

  /** Payment error */
  payment: (decline_code: string) =>
    track('error_payment', { decline_code }),
}

// Generic track function for custom events
export { track as trackEvent }
